import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  apiLoadCopyCampaignModal,
  apiOrganizerCopyCampaign,
  apiOrganizerValidateCopyCampaign,
} from 'src/api/account/organizer';
import { UiCleanInput } from 'src/components/UI/inputs/CleanInput';
import { UiModal } from 'src/components/UI/modal/UiModal';
import { UiToast } from 'src/components/UI/toasts';
import { BaseApiResponseType } from 'src/models/BaseApi';
import {
  MyCampaignCopyResponseType,
  MyCampaignCopyType,
  MyCampaignLoadCopyResponseType,
  MyCampaignsListItemType,
} from 'src/models/Campaigns/MyCampaigns';
import { DonationPageListItemType } from 'src/models/DonationPage/List';
import { getErrorTextFromPreparedApiResponse } from 'src/utils/errors';
import { hardRedirect } from 'src/utils/hardRedirect';
import { httpResponseEither } from 'src/utils/httpResponse';
import { useTHelper } from 'src/utils/i18n';
import { useStateForm } from 'src/utils/stateForm';
import { useOneRequest } from 'src/utils/hooks/useOneRequest';
import { useSelector } from 'react-redux';
import { deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';

type FormValues = {
  NewCampaignName: string;
};

type Props = {
  item: MyCampaignsListItemType | DonationPageListItemType | null;
  hideModal: () => void;
  donationPageList?: boolean;
};

export const MyCampaignsModalCopy: React.FC<Props> = ({ item, hideModal, donationPageList }: Props) => {
  const { t } = useTranslation();

  const tHelper = useTHelper('myCampaigns.modals.copy');
  const tHelperUnlimited = useTHelper('myCampaigns.modals.unlimited');

  const { CampaignTerminologySingular } = useSelector(deCommonCurrentPartnerSelector);

  const ref = useRef(true);

  const formProps = useStateForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      NewCampaignName: '',
    },
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const catchFn = useCallback(
    (errors: Error) => {
      if (ref.current) {
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error('httpError: ', errors);
        UiToast.error(t('common.httpError'));
      }
    },
    [t]
  );

  const { loading: modelLoading, response: copyModel } = useOneRequest<MyCampaignLoadCopyResponseType>(
    apiLoadCopyCampaignModal,
    { initialCall: !!item, data: { PermanentUrl: item?.PermanentUrl } }
  );

  const [showUnlimitedModal, setShowUnlimitedModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(true);

  if (!item) {
    hideModal();
    return null;
  }

  const entitySingularLower = item?.ProjectTerminology.Singular.toLowerCase() || '';

  const copyCampaign = () => {
    const model: MyCampaignCopyType = {
      ID: item.ID,
      NewCampaignName: formProps.getValue('NewCampaignName'),
    };

    setLoading(true);

    const leftFN = ({ errors }: BaseApiResponseType) => {
      setErrors(getErrorTextFromPreparedApiResponse(errors));
      setLoading(false);
    };

    /** made as promise because if an inner nested logic */
    apiOrganizerValidateCopyCampaign(model)
      .then((response) => {
        if (ref.current) {
          httpResponseEither<MyCampaignCopyResponseType>(() => {
            apiOrganizerCopyCampaign(model)
              .then((copyResponse) => {
                httpResponseEither<MyCampaignCopyType>(() => {
                  hardRedirect(
                    `/campaigns/${donationPageList ? 'donationpagesetup' : 'setup'}/${
                      copyResponse.PermanentUrl || 'noPermanentUrl'
                    }`
                  );
                }, leftFN)(copyResponse);
              })
              .catch(catchFn);
          }, leftFN)(response);
        }
      })
      .catch(catchFn);
  };

  return (
    <>
      {showCopyModal && (
        <UiModal
          header={tHelper('header', { entitySingularLower })}
          successBtnText={tHelper('successBtnText')}
          successLoading={loading}
          onSuccess={formProps.onSubmit(() => {
            if (item?.ID) {
              if (copyModel?.HasRestrictedFeatures) {
                setShowUnlimitedModal(true);
                setShowCopyModal(false);
              } else {
                copyCampaign();
              }
            }
          })}
          modalClassName="myCampaignCopyModal"
          onHide={() => {
            if (showUnlimitedModal) {
              setShowCopyModal(false);
            } else {
              hideModal();
            }
          }}
          validationErrors={errors}
          onCloseValidationErrors={() => setErrors([])}
          loading={modelLoading}
          onCancel={hideModal}
        >
          <p>{tHelper('body', { entitySingularLower })}</p>
          <UiCleanInput
            formProps={formProps}
            name="NewCampaignName"
            label={tHelper('labelNewName', { entitySingularLower })}
            placeholder={tHelper('placeholderNewName', { entitySingularLower })}
            errorLabel={tHelper('placeholderNewName', { entitySingularLower })}
            maxLength={75}
            required
            material
          />
        </UiModal>
      )}

      {showUnlimitedModal && (
        <UiModal
          onHide={() => hideModal()}
          header={tHelperUnlimited('header')}
          successBtnText={tHelper('successBtnText')}
          successLoading={loading}
          onSuccess={copyCampaign}
        >
          <p>
            {tHelperUnlimited('body', {
              percent: copyModel?.ServiceFeePercent,
              campaignSingularTerminology: CampaignTerminologySingular,
            })}
          </p>
        </UiModal>
      )}
    </>
  );
};
